import { Component } from '@theme/component';
import {
  center,
  closest,
  clamp,
  getVisibleElements,
  mediaQueryLarge,
  prefersReducedMotion,
  preventDefault,
  viewTransition,
  scheduler,
} from '@theme/utilities';
import { Scroller, scrollIntoView } from '@theme/scrolling';
import { SlideshowSelectEvent } from '@theme/events';

// The threshold for determining visibility of slides.
const SLIDE_VISIBLITY_THRESHOLD = 0.7;

/**
 * Slideshow custom element that allows sliding between content.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [current]
 * @property {HTMLElement[]} [thumbnails]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class Slideshow extends Component {
  static get observedAttributes() {
    return ['initial-slide'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    // Collection page filtering can morph slideshow galleries in place,
    // updating initial-slide and the hidden state of individual slides.
    if (name === 'initial-slide' && oldValue !== newValue) {
      queueMicrotask(() => {
        if (!this.isConnected || !this.#scroll || !this.refs.slides?.length) {
          return;
        }

        this.#ensureActiveSlideVisible();

        const index = clamp(
          parseInt(newValue, 10) || 0,
          0,
          this.refs.slides.length - 1
        );

        const slideId = this.refs.slides[index]?.getAttribute('slide-id');

        if (slideId) {
          this.select(
            { id: slideId },
            undefined,
            { animate: false }
          );
        }
      });
    }
  }

  requiredRefs = ['scroller'];

  async connectedCallback() {
    super.connectedCallback();

    // Wait for any in-progress view transitions to finish.
    if (viewTransition.current) {
      await viewTransition.current;

      // The slideshow may have been removed before the transition finished.
      if (!this.isConnected) return;
    }

    const { scroller, slideshowContainer } = this.refs;

    this.#scroll = new Scroller(scroller, {
      onScroll: this.#handleScroll,
      onScrollStart: this.#onTransitionInit,
      onScrollEnd: this.#onTransitionEnd,
    });

    scroller.addEventListener('mousedown', this.#handleMouseDown);

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('pointerenter', this.#handlePointerEnter);
    document.addEventListener(
      'visibilitychange',
      this.#handleVisibilityChange
    );

    this.#updateControlsVisibility();

    this.disabled = this.isNested || this.disabled;

    this.resume();

    this.current = this.initialSlideIndex;

    /*
     * Shopify collection filtering can morph existing product cards rather
     * than reconnecting their slideshow custom elements. Watch for those
     * changes and repair the invalid state where every slide is hidden.
     */
    if (slideshowContainer) {
      this.#slideVisibilityObserver = new MutationObserver(() => {
        queueMicrotask(() => {
          this.#ensureActiveSlideVisible();
        });
      });

      this.#slideVisibilityObserver.observe(slideshowContainer, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['hidden'],
      });
    }

    // Batch reads and writes to the DOM.
    scheduler.schedule(() => {
      this.#ensureActiveSlideVisible();

      let visibleSlidesAmount = 0;
      const initialSlideId =
        this.initialSlide?.getAttribute('slide-id');

      if (this.initialSlideIndex !== 0 && initialSlideId) {
        this.select(
          { id: initialSlideId },
          undefined,
          { animate: false }
        );

        visibleSlidesAmount = 1;
      } else {
        visibleSlidesAmount = this.#updateVisibleSlides();

        if (visibleSlidesAmount === 0) {
          this.#ensureActiveSlideVisible();
          visibleSlidesAmount = 1;
        }
      }

      this.#resizeObserver = new ResizeObserver(async () => {
        if (viewTransition.current) {
          await viewTransition.current;
        }

        this.#ensureActiveSlideVisible();

        if (visibleSlidesAmount > 1) {
          this.#updateVisibleSlides();
        }

        if (this.hasAttribute('auto-hide-controls')) {
          this.#updateControlsVisibility();
        }
      });

      if (this.refs.slideshowContainer) {
        this.#resizeObserver.observe(
          this.refs.slideshowContainer
        );
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const { scroller } = this.refs;

    scroller?.removeEventListener(
      'mousedown',
      this.#handleMouseDown
    );

    this.removeEventListener('mouseenter', this.suspend);
    this.removeEventListener('mouseleave', this.resume);
    this.removeEventListener(
      'pointerenter',
      this.#handlePointerEnter
    );

    document.removeEventListener(
      'visibilitychange',
      this.#handleVisibilityChange
    );

    this.#scroll?.destroy();

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }

    if (this.#slideVisibilityObserver) {
      this.#slideVisibilityObserver.disconnect();
    }
  }

  /** Indicates whether the slideshow is nested inside another slideshow. */
  get isNested() {
    return (
      this.parentElement?.closest('slideshow-component') !== null
    );
  }

  get initialSlide() {
    return this.refs.slides?.[this.initialSlideIndex];
  }

  /**
   * Ensures the slideshow never remains in an invalid state where every
   * slide is hidden.
   */
  #ensureActiveSlideVisible() {
    const slides = this.refs.slides;

    if (!slides?.length) return;

    const hasVisibleSlide = slides.some(
      (slide) =>
        !slide.hasAttribute('hidden') ||
        slide.hasAttribute('reveal')
    );

    if (hasVisibleSlide) return;

    const index = clamp(
      this.initialSlideIndex,
      0,
      slides.length - 1
    );

    const slide = slides[index] ?? slides[0];

    if (!slide) return;

    slide.setAttribute('reveal', '');
    slide.setAttribute('aria-hidden', 'false');

    this.current = slides.indexOf(slide);

    if (this.#scroll) {
      this.#scroll.to(slide, { instant: true });
    }
  }

  /**
   * Selects a slide based on the input index.
   *
   * @param {number|string|{id: string}} input
   * @param {Event} [event]
   * @param {Object} [options]
   * @param {boolean} [options.animate=true]
   */
  async select(input, event, options = {}) {
    if (this.#disabled || !this.refs.slides?.length) return;

    let requestedSlide;

    let requestedIndex = (() => {
      if (typeof input === 'number') {
        return input;
      }

      if (typeof input === 'string') {
        return parseInt(input, 10);
      }

      if ('id' in input) {
        requestedSlide = this.refs.slides.find(
          (slide) =>
            slide.getAttribute('slide-id') == input.id
        );

        if (!requestedSlide) return;

        /*
         * Variant slides can deliberately carry hidden. reveal temporarily
         * makes the requested slide part of the active slideshow.
         */
        if (requestedSlide.hasAttribute('hidden')) {
          requestedSlide.setAttribute('reveal', '');
          requestedSlide.setAttribute(
            'aria-hidden',
            'false'
          );
        }

        return this.slides?.indexOf(requestedSlide);
      }
    })();

    const { current } = this;

    if (
      requestedIndex === undefined ||
      Number.isNaN(requestedIndex)
    ) {
      return;
    }

    /*
     * Filtering can reselect the slide already stored as current while
     * that slide is hidden. Keep it revealed instead of removing reveal
     * and returning with no visible image.
     */
    if (requestedIndex === current) {
      const currentSlide =
        requestedSlide ??
        this.slides?.[requestedIndex] ??
        this.refs.slides[requestedIndex];

      if (currentSlide?.hasAttribute('hidden')) {
        currentSlide.setAttribute('reveal', '');
      }

      currentSlide?.setAttribute(
        'aria-hidden',
        'false'
      );

      return;
    }

    /*
     * Clear temporary reveal states only after confirming that the
     * slideshow is moving to a different slide. Do not clear the reveal
     * state from the requested slide.
     */
    for (const slide of this.refs.slides) {
      if (
        slide !== requestedSlide &&
        slide.hasAttribute('reveal')
      ) {
        slide.removeAttribute('reveal');
        slide.setAttribute('aria-hidden', 'true');
      }
    }

    if (requestedSlide?.hasAttribute('hidden')) {
      requestedSlide.setAttribute('reveal', '');
      requestedSlide.setAttribute('aria-hidden', 'false');
    }

    const { slides } = this;

    if (!slides?.length) {
      this.#ensureActiveSlideVisible();
      return;
    }

    if (!this.infinite) {
      requestedIndex = clamp(
        requestedIndex,
        0,
        slides.length - 1
      );
    }

    event?.preventDefault();

    const { animate = true } = options;
    const lastIndex = slides.length - 1;

    let index = requestedIndex;

    if (requestedIndex < 0) {
      index = lastIndex;
    } else if (requestedIndex > lastIndex) {
      index = 0;
    }

    const isAdjacentSlide =
      Math.abs(index - current) <= 1 &&
      requestedIndex >= 0 &&
      requestedIndex <= lastIndex;

    const { visibleSlides } = this;
    const instant = prefersReducedMotion() || !animate;

    /*
     * If the jump is more than one slide, or the slideshow loops, use the
     * temporary placeholder and reorder method to keep the animation short.
     */
    if (
      !instant &&
      !isAdjacentSlide &&
      visibleSlides.length === 1
    ) {
      this.#disabled = true;

      await this.#scroll.finished;

      const targetSlide = slides[index];
      const currentSlide = slides[current];

      if (!targetSlide || !currentSlide) {
        this.#disabled = false;
        return;
      }

      const placeholder =
        document.createElement('slideshow-slide');

      targetSlide.before(placeholder);

      if (requestedIndex < current) {
        currentSlide.before(targetSlide);
      } else {
        currentSlide.after(targetSlide);
      }

      if (current === 0) {
        this.#scroll.to(currentSlide, {
          instant: true,
        });
      }

      queueMicrotask(async () => {
        await this.#scroll.finished;

        this.#disabled = false;

        placeholder.replaceWith(targetSlide);

        this.#scroll.to(targetSlide, {
          instant: true,
        });
      });
    }

    const slide = slides[index];

    if (!slide) {
      this.#ensureActiveSlideVisible();
      return;
    }

    const previousIndex = this.current;

    slide.setAttribute('aria-hidden', 'false');

    this.#scroll.to(slide, { instant });

    this.current = this.slides?.indexOf(slide) || 0;

    this.#centerSelectedThumbnail(
      index,
      instant ? 'instant' : 'smooth'
    );

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: event != null,
        trigger: 'select',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  }

  /**
   * Advances to the next slide.
   *
   * @param {Event} [event]
   * @param {Object} [options]
   * @param {boolean} [options.animate=true]
   */
  next(event, options) {
    event?.preventDefault();
    this.select(this.nextIndex, event, options);
  }

  /**
   * Goes back to the previous slide.
   *
   * @param {Event} [event]
   * @param {Object} [options]
   * @param {boolean} [options.animate=true]
   */
  previous(event, options) {
    event?.preventDefault();
    this.select(this.previousIndex, event, options);
  }

  /**
   * Starts automatic slide playback.
   *
   * @param {number} [interval]
   */
  play(interval = this.autoplayInterval) {
    if (this.#interval) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) {
        return;
      }

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    if (value) {
      this.setAttribute('paused', '');
    } else {
      this.removeAttribute('paused');
    }
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  /**
   * The current slide index.
   *
   * @type {number}
   */
  #current = 0;

  get current() {
    return this.#current;
  }

  /**
   * Sets the current slide index and updates the DOM.
   *
   * @param {number} value
   */
  set current(value) {
    const {
      current,
      thumbnails,
      dots,
      slides,
      previous,
      next,
    } = this.refs;

    this.#current = value;

    if (current) {
      current.textContent = `${value + 1}`;
    }

    for (const controls of [thumbnails, dots]) {
      controls?.forEach((element, index) => {
        element.setAttribute(
          'aria-selected',
          `${index === value}`
        );
      });
    }

    if (previous) {
      previous.disabled = Boolean(
        !this.infinite && value === 0
      );
    }

    if (next) {
      next.disabled = Boolean(
        !this.infinite &&
          slides &&
          this.nextIndex >= slides.length
      );
    }
  }

  get infinite() {
    return this.getAttribute('infinite') != null;
  }

  get visibleSlides() {
    return getVisibleElements(
      this.refs.scroller,
      this.slides,
      SLIDE_VISIBLITY_THRESHOLD,
      'x'
    );
  }

  get previousIndex() {
    const { current, visibleSlides } = this;
    const modifier =
      visibleSlides.length > 1
        ? visibleSlides.length
        : 1;

    return current - modifier;
  }

  get nextIndex() {
    const { current, visibleSlides } = this;
    const modifier =
      visibleSlides.length > 1
        ? visibleSlides.length
        : 1;

    return current + modifier;
  }

  get atStart() {
    const { current, slides } = this;

    return slides?.length
      ? current === 0
      : false;
  }

  get atEnd() {
    const { current, slides } = this;

    return slides?.length
      ? current === slides.length - 1
      : false;
  }

  /**
   * Sets the disabled attribute.
   *
   * @param {boolean} value
   */
  set disabled(value) {
    this.setAttribute('disabled', String(value));
  }

  /**
   * Whether the slideshow is disabled.
   */
  get disabled() {
    return (
      this.getAttribute('disabled') === 'true' ||
      (
        this.hasAttribute('mobile-disabled') &&
        !mediaQueryLarge.matches
      )
    );
  }

  /**
   * Indicates whether the slideshow is temporarily disabled.
   *
   * @type {boolean}
   */
  #disabled = false;

  /**
   * The interval ID for automatic playback.
   *
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * The Scroller instance that manages scrolling.
   *
   * @type {Scroller}
   */
  #scroll;

  /**
   * Monitors slideshow sizing.
   *
   * @type {ResizeObserver|undefined}
   */
  #resizeObserver;

  /**
   * Monitors filtered-result morphs that change slide visibility.
   *
   * @type {MutationObserver|undefined}
   */
  #slideVisibilityObserver;

  /**
   * Callback invoked on user-initiated scroll.
   */
  #handleScroll = () => {
    const previousIndex = this.#current;
    const index = this.#sync();

    if (index === previousIndex) return;

    const slide = this.slides?.[index];

    if (!slide) return;

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: true,
        trigger: 'scroll',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  };

  #onTransitionInit = () => {
    this.setAttribute('transitioning', '');
  };

  #onTransitionEnd = () => {
    this.#ensureActiveSlideVisible();
    this.#updateVisibleSlides();
    this.removeAttribute('transitioning');
  };

  /**
   * Synchronises the scroll position and current slide index.
   *
   * @returns {number}
   */
  #sync = () => {
    const { slides } = this;

    if (!slides?.length) {
      this.#ensureActiveSlideVisible();
      return (this.current = 0);
    }

    const visibleSlides = this.visibleSlides;

    if (!visibleSlides.length) {
      this.#ensureActiveSlideVisible();
      return this.current;
    }

    const { axis } = this.#scroll;
    const { scroller } = this.refs;

    const centers = visibleSlides.map((slide) =>
      center(slide, axis)
    );

    const referencePoint =
      visibleSlides.length > 1
        ? scroller.getBoundingClientRect()[axis]
        : center(scroller, axis);

    const closestCenter = closest(
      centers,
      referencePoint
    );

    const closestVisibleSlide =
      visibleSlides[centers.indexOf(closestCenter)];

    if (!closestVisibleSlide) {
      return (this.current = 0);
    }

    const index = slides.indexOf(
      closestVisibleSlide
    );

    return (this.current = index);
  };

  #dragging = false;

  /**
   * Handles the mousedown event used to begin dragging.
   *
   * @param {MouseEvent} event
   */
  #handleMouseDown = (event) => {
    const { slides } = this;

    if (!slides || slides.length <= 1) return;
    if (!(event.target instanceof Element)) return;
    if (this.disabled || this.#dragging) return;

    if (event.target.closest('model-viewer')) {
      return;
    }

    event.preventDefault();

    const { axis } = this.#scroll;
    const startPosition = event[axis];

    const controller = new AbortController();
    const { signal } = controller;

    const startTime = performance.now();

    let previous = startPosition;
    let velocity = 0;
    let moved = false;
    let distanceTravelled = 0;

    this.#dragging = true;

    /**
     * @param {PointerEvent} event
     */
    const onPointerMove = (event) => {
      const current = event[axis];
      const initialDelta = startPosition - current;

      if (!initialDelta) return;

      if (!moved) {
        moved = true;

        this.setPointerCapture(event.pointerId);

        document.addEventListener(
          'click',
          preventDefault,
          {
            once: true,
            signal,
          }
        );

        const movingRight = initialDelta < 0;
        const movingLeft = initialDelta > 0;

        const closestSlideshow =
          this.parentElement?.closest(
            'slideshow-component'
          );

        const isNested =
          closestSlideshow instanceof Slideshow &&
          closestSlideshow !== this;

        const cannotMoveInDirection =
          (movingRight && this.atStart) ||
          (movingLeft && this.atEnd);

        if (isNested && cannotMoveInDirection) {
          controller.abort();
          return;
        }

        this.pause();
        this.setAttribute('dragging', '');
      }

      event.stopImmediatePropagation();

      const delta = previous - current;
      const timeDelta =
        performance.now() - startTime;

      velocity = Math.round(
        (delta / timeDelta) * 1000
      );

      previous = current;
      distanceTravelled += Math.abs(delta);

      this.#scroll.by(delta, {
        instant: true,
      });
    };

    /**
     * @param {PointerEvent} event
     */
    const onPointerUp = async (event) => {
      controller.abort();

      const { current, slides } = this;
      const { scroller } = this.refs;

      this.#dragging = false;

      if (!slides?.length || !scroller) return;

      const direction = Math.sign(velocity);
      const next = this.#sync();

      const modifier =
        current !== next ||
        Math.abs(velocity) < 10 ||
        distanceTravelled < 10
          ? 0
          : direction;

      const newIndex = clamp(
        next + modifier,
        0,
        slides.length - 1
      );

      const newSlide = slides[newIndex];
      const currentIndex = this.current;

      if (!newSlide) {
        throw new Error(
          `Slide not found at index ${newIndex}`
        );
      }

      this.#scroll.to(newSlide);

      this.removeAttribute('dragging');

      if (this.hasPointerCapture(event.pointerId)) {
        this.releasePointerCapture(
          event.pointerId
        );
      }

      this.#centerSelectedThumbnail(newIndex);

      this.dispatchEvent(
        new SlideshowSelectEvent({
          index: newIndex,
          previousIndex: currentIndex,
          userInitiated: true,
          trigger: 'drag',
          slide: newSlide,
          id: newSlide.getAttribute('slide-id'),
        })
      );

      this.current = newIndex;

      await this.#scroll.finished;

      if (this.#dragging) return;

      this.#scroll.snap = true;
      this.resume();
    };

    this.#scroll.snap = false;

    document.addEventListener(
      'pointermove',
      onPointerMove,
      { signal }
    );

    document.addEventListener(
      'pointerup',
      onPointerUp,
      { signal }
    );

    document.addEventListener(
      'pointerdown',
      onPointerUp,
      { signal }
    );

    document.addEventListener(
      'pointercancel',
      onPointerUp,
      { signal }
    );

    document.addEventListener(
      'pointercapturelost',
      onPointerUp,
      { signal }
    );
  };

  #handlePointerEnter = () => {
    this.setAttribute('actioned', '');
  };

  get slides() {
    return this.refs.slides?.filter(
      (slide) =>
        !slide.hasAttribute('hidden') ||
        slide.hasAttribute('reveal')
    );
  }

  /**
   * The initial slide index.
   */
  get initialSlideIndex() {
    const initialSlide =
      this.getAttribute('initial-slide');

    if (initialSlide == null) return 0;

    const parsedIndex = parseInt(
      initialSlide,
      10
    );

    return Number.isNaN(parsedIndex)
      ? 0
      : parsedIndex;
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.pause();
    } else {
      this.resume();
    }
  };

  #updateControlsVisibility() {
    if (
      !this.hasAttribute('auto-hide-controls')
    ) {
      return;
    }

    const {
      scroller,
      slideshowControls,
    } = this.refs;

    if (
      !(slideshowControls instanceof HTMLElement)
    ) {
      return;
    }

    slideshowControls.hidden =
      scroller.scrollWidth <= scroller.offsetWidth;
  }

  /**
   * Centres the selected thumbnail.
   *
   * @param {number} index
   * @param {ScrollBehavior} [behavior]
   */
  #centerSelectedThumbnail(
    index,
    behavior = 'smooth'
  ) {
    const selectedThumbnail =
      this.refs.thumbnails?.[index];

    if (!selectedThumbnail) return;

    const { thumbnailsContainer } = this.refs;

    if (
      !thumbnailsContainer ||
      !(thumbnailsContainer instanceof HTMLElement)
    ) {
      return;
    }

    const { slideshowControls } = this.refs;

    if (
      !slideshowControls ||
      !(slideshowControls instanceof HTMLElement)
    ) {
      return;
    }

    scrollIntoView(selectedThumbnail, {
      ancestor: thumbnailsContainer,
      behavior,
      block: 'center',
      inline: 'center',
    });
  }

  #updateVisibleSlides() {
    this.#ensureActiveSlideVisible();

    const { slides } = this;

    if (!slides?.length) return 0;

    const visibleSlides = this.visibleSlides;

    scheduler.schedule(() => {
      slides.forEach((slide) => {
        const isVisible =
          visibleSlides.includes(slide);

        slide.setAttribute(
          'aria-hidden',
          `${!isVisible}`
        );
      });
    });

    return visibleSlides.length;
  }
}

if (
  !customElements.get('slideshow-component')
) {
  customElements.define(
    'slideshow-component',
    Slideshow
  );
}